/**
 * Keeps resolved media links stable for as long as they are valid.
 *
 * A media link is a pre-signed URL, and the server signs a new one on every
 * request — same file, different query string. To the browser that is a
 * different URL, so its cache never hit and every visit to a listing
 * downloaded every photo again. Handing back the link we already have, until
 * shortly before it expires, lets the browser serve the image from its cache.
 */
type Link = { id: string; link: string };
type Entry = { link: string; expiresAt: number; safety: number };

const STORE_KEY = "mediaLinks:v2";
/**
 * Stop reusing a link this long before it expires — long enough for an image
 * that starts loading now to finish. A fixed five minutes here once matched
 * the links' entire lifetime (the server signs them for 300s), so nothing was
 * ever reused; the margin is now a fraction of however long the link lives.
 */
const safetyFor = (lifetimeMs: number) =>
  Math.min(60 * 1000, Math.max(10 * 1000, lifetimeMs * 0.15));
/** Used when the link does not say how long it lives. */
const DEFAULT_TTL_MS = 10 * 60 * 1000;

let memory: Record<string, Entry> | null = null;

function load(): Record<string, Entry> {
  if (memory) return memory;
  try {
    memory = JSON.parse(sessionStorage.getItem(STORE_KEY) || "{}") || {};
  } catch {
    memory = {};
  }
  return memory as Record<string, Entry>;
}

function persist() {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(memory));
  } catch {
    // Storage full or unavailable: the in-memory copy still serves this tab.
  }
}

/** When a pre-signed link stops working, read from its own query string. */
function expiryOf(link: string): number {
  try {
    const q = new URL(link).searchParams;
    const seconds = Number(q.get("X-Amz-Expires"));
    const signed = q.get("X-Amz-Date"); // 20260920T101500Z
    if (seconds && signed) {
      const iso = signed.replace(
        /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
        "$1-$2-$3T$4:$5:$6Z",
      );
      const at = Date.parse(iso);
      if (!Number.isNaN(at)) return at + seconds * 1000;
    }
    const unix = Number(q.get("Expires"));
    if (unix) return unix * 1000;
  } catch {
    // Not a URL we can read: fall through to the default.
  }
  return Date.now() + DEFAULT_TTL_MS;
}

/**
 * Wraps a "get links for these ids" call: ids with a still-valid link are
 * answered from the cache, and only the rest go to the server.
 */
export async function withLinkCache(
  ids: string[],
  variant: string,
  fetchLinks: (missing: string[]) => Promise<{ links?: Link[] }>,
): Promise<{ links: Link[] }> {
  const store = load();
  const now = Date.now();
  const key = (id: string) => `${variant}:${id}`;
  const wanted = [...new Set(ids.filter(Boolean))];
  const missing = wanted.filter((id) => {
    const hit = store[key(id)];
    return !hit || hit.expiresAt - (hit.safety ?? 0) <= now;
  });

  if (missing.length > 0) {
    const res = await fetchLinks(missing);
    (res.links ?? []).forEach((l) => {
      if (l?.id && l.link) {
        const expiresAt = expiryOf(l.link);
        store[key(l.id)] = {
          link: l.link,
          expiresAt,
          safety: safetyFor(expiresAt - now),
        };
      }
    });
    // Drop what has expired so the store does not grow without bound.
    Object.keys(store).forEach((k) => {
      if (store[k].expiresAt <= now) delete store[k];
    });
    persist();
  }

  return {
    links: wanted
      .filter((id) => store[key(id)])
      .map((id) => ({ id, link: store[key(id)].link })),
  };
}
